/* © CNES */
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import { TabPanel, TabView } from 'primereact/tabview';
import { Tag } from 'primereact/tag';
import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSavedProducts } from '../../../api/cart/Cart';
import { getAllProcess } from '../../../api/process/process';
import { useGetProcessings } from '../../../api/processings/processings';
import { ConfigContext } from '../../../context/ConfigContext';
import { CartProduct } from '../../../models/CartProduct';
import { CollectionDetails } from '../../../models/CollectionDetails';
import { Processing } from '../../../models/Processing';
import { ProductsAdded } from '../../../models/ProductsAdded';
import { getProductsAddedObject } from '../../shared/Utils';
import { InnerAdded } from '../InnerAdded/InnerAdded';
import { InnerProcessings } from '../InnerProcessings/InnerProcessings';
import { InnerRecoveringFromArchive } from '../innerRecoveringFromArchive/InnerRecoveringFromArchive';
import './MyDataTabs.scss';

/**
 * MyData Tabs dispays Tabs and tabs content for MyData.
 * @param activeIndex active tab index
 * @param setActiveIndex function
 * @param setDownloadProgress function
 * @returns Element.
 */
export const MyDataTabs = ({
  activeIndex,
  setActiveIndex,
  setDownloadProgress,
}: {
  activeIndex: number;
  setActiveIndex: Function;
  setDownloadProgress: Function;
}) => {
  const context = useContext(ConfigContext);
  const navigate = useNavigate();

  const { data, refetch } = useGetProcessings(
    {
      isEnabled: !!context.userLoggedIn.email,
    },
    navigate,
    context.setError400
  );
  const [processings, setProcessings] = useState<Processing[]>([]);

  const [processingCount, setProcessingCount] = useState(0);

  const [doRefetchJobs, setDoRefetchJobs] = useState(false);
  const [productsRecoveringFromArchive, setProductsRecoveringFromArchive] =
    useState<ProductsAdded>({} as ProductsAdded);
  const [productsAdded, setProductsAdded] = useState<ProductsAdded>(
    {} as ProductsAdded
  );

  // refetch processings after Launch processes click
  useEffect(() => {
    if (doRefetchJobs) {
      refetch();
    }
  }, [doRefetchJobs, refetch]);

  /**
   * setProcessings and count on data change.
   */
  useEffect(() => {
    // multiple sort on jobID and start_datetime so jobs stay at the same position in the processings list after refetch.
    setProcessings(
      (data?.jobs as Processing[])?.sort(
        (a: Processing, b: Processing) =>
          moment(b.job_start_datetime).toDate().getTime() -
            moment(a.job_start_datetime).toDate().getTime() ||
          a.jobID.localeCompare(b.jobID)
      )
    );
    setProcessingCount(data?.jobs.length);
  }, [data, setProcessingCount]);

  const templateHeader = (title: string, count: number) => {
    return (
      <div className="TemplateHeader">
        <p className="mr-2">
          {title} {!!count && `(${count})`}
        </p>
        {!!count && (
          <Tag className="TemplateTag" severity="warning" value="New" />
        )}
      </div>
    );
  };

  /**
   * Call getSavedProducts API to get the stored Cart products
   * @param queryKey contains the following param:
   * @param user current user
   * @returns getProducts all stored Cart products
   */
  const { data: savedProducts, refetch: refetchGetSavedProduct } = useQuery({
    queryKey: [
      'GetSavedProducts',
      {
        user: context.userLoggedIn.email,
      },
    ],
    queryFn: getSavedProducts,
    retry: false,
  });

  /**
   * @description Trigger GetSavedProducts API to get all stored products and use it to fill the context
   * @requires data
   */
  useEffect(() => {
    if (savedProducts && savedProducts.length > 0) {
      const filterProductsByStorage = (storageType: string) =>
        savedProducts.filter(
          (cartProduct: CartProduct) => cartProduct.storage === storageType
        );

      const addedTabItems = filterProductsByStorage('Datalake Tier 2');
      const recoveringFromArchiveTabItems =
        filterProductsByStorage('Datalake Tier 3');

      const allDataWithStorage = [
        ...addedTabItems,
        ...recoveringFromArchiveTabItems,
      ];
      const allMyDataProducts = getProductsAddedObject(allDataWithStorage);

      setProductsAdded(getProductsAddedObject(addedTabItems));
      setProductsRecoveringFromArchive(
        getProductsAddedObject(recoveringFromArchiveTabItems)
      );
      context.setProductsAdded(allMyDataProducts);
    } else {
      const productsToAdd = {
        products: [],
        results: [],
        levelSet: new Set(),
        satelliteSet: new Set(),
        collectionsDetails: [] as CollectionDetails[],
      } as ProductsAdded;
      context.setProductsAdded(productsToAdd);
    }
    // eslint-disable-next-line
  }, [savedProducts]);

  const { data: dataProcess } = useQuery({
    queryKey: ['Processes', navigate, context.setError400],
    queryFn: getAllProcess,
  });

  return (
    <>
      <div className="MyDataTabs">
        <TabView
          activeIndex={activeIndex}
          onTabChange={(e) => setActiveIndex(e.index)}
        >
          <TabPanel
            header={templateHeader(
              'Added',
              productsAdded?.products?.length ?? 0
            )}
          >
            <InnerAdded
              productsAdded={productsAdded}
              setProductsAdded={setProductsAdded}
              setActiveIndex={setActiveIndex}
              setDoRefetchJobs={setDoRefetchJobs}
              listProcess={dataProcess?.processes}
              setDownloadProgress={setDownloadProgress}
              refetchGetSavedProduct={refetchGetSavedProduct}
            />
          </TabPanel>
          <TabPanel header={templateHeader('Processings', processingCount)}>
            <InnerProcessings
              processings={processings}
              listProcess={dataProcess?.processes}
              setDoRefetchJobs={setDoRefetchJobs}
              setDownloadProgress={setDownloadProgress}
            />
          </TabPanel>
          <TabPanel
            header={templateHeader(
              'Recovering from Archive',
              productsRecoveringFromArchive?.products?.length ?? 0
            )}
          >
            <InnerRecoveringFromArchive
              productsAdded={productsRecoveringFromArchive}
            />
          </TabPanel>
        </TabView>
      </div>
    </>
  );
};
